export const VIEWPORT_TRIANGULATION_MARKER = 'KAIROS_VIEWPORT_TRIANGULATION_CROSSHAIR_MIDDLE_CLICK';
export const VIEWPORT_TRIANGULATION_DRAG_MARKER = 'KAIROS_VIEWPORT_TRIANGULATION_DRAG_MARKER';
export const VIEWPORT_TRIANGULATION_PARALLEL_SYNC_GUARD = 'KAIROS_VIEWPORT_TRIANGULATION_PARALLEL_SYNC_GUARD';
export const VIEWPORT_REFERENCE_LINE_MARKER = 'KAIROS_QOL_GEOMETRY_REFERENCE_LINES';

const finiteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const clamp01 = (value) => {
  const n = finiteNumber(value);
  if (n == null) return null;
  return Math.max(0, Math.min(1, n));
};

const clampPositiveInt = (value) => {
  const n = finiteNumber(value);
  if (n == null || n <= 0) return null;
  return Math.max(1, Math.round(n));
};

const vectorDot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const vectorSub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const vectorAdd = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const vectorScale = (a, scalar) => [a[0] * scalar, a[1] * scalar, a[2] * scalar];
const vectorCross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const vectorLength = (a) => Math.sqrt(vectorDot(a, a));

const normalizeVector3 = (v) => {
  if (!Array.isArray(v) || v.length !== 3) return null;
  const length = vectorLength(v);
  if (!Number.isFinite(length) || length < 1e-6) return null;
  return v.map(component => component / length);
};

const parseNumberList = (raw, expectedLength) => {
  const value = raw?.Value ?? raw;
  const parts = Array.isArray(value) ? value : String(value ?? '').split(/[\\,\s]+/).filter(Boolean);
  if (parts.length !== expectedLength) return null;
  const nums = parts.map(finiteNumber);
  return nums.every(n => n != null) ? nums : null;
};

const readFirst = (source, keys) => {
  for (const key of keys) {
    if (source && Object.prototype.hasOwnProperty.call(source, key)) return source[key];
  }
  return undefined;
};

const readString = (source, keys) => {
  const raw = readFirst(source, keys);
  const value = raw?.Value ?? raw;
  const first = Array.isArray(value) ? value[0] : value;
  const text = String(first ?? '').trim();
  return text || null;
};

export const normalizeTriangulationPoint = (point, { clamp = true } = {}) => {
  const rawX = finiteNumber(point?.x);
  const rawY = finiteNumber(point?.y);
  if (rawX == null || rawY == null) return null;
  if (!clamp && (rawX < 0 || rawX > 1 || rawY < 0 || rawY > 1)) return null;
  return clamp ? { x: clamp01(rawX), y: clamp01(rawY) } : { x: rawX, y: rawY };
};

export const normalizeTriangulationGeometry = (geometry = {}, fallbackDimensions = {}) => {
  const imagePositionPatient = parseNumberList(readFirst(geometry, ['imagePositionPatient', 'ImagePositionPatient', 'imagePosition', 'ipp']), 3);
  const imageOrientationPatient = parseNumberList(readFirst(geometry, ['imageOrientationPatient', 'ImageOrientationPatient', 'imageOrientation', 'iop']), 6);
  const pixelSpacing = parseNumberList(readFirst(geometry, ['pixelSpacing', 'PixelSpacing', 'pixel_spacing', 'ImagerPixelSpacing', 'imagerPixelSpacing']), 2);
  const frameOfReferenceUID = readString(geometry, ['frameOfReferenceUID', 'FrameOfReferenceUID', 'frameOfReferenceUid', 'forUID', 'forUid']);
  const rows = clampPositiveInt(readFirst(geometry, ['rows', 'Rows', 'height']) ?? fallbackDimensions.rows ?? fallbackDimensions.height);
  const columns = clampPositiveInt(readFirst(geometry, ['columns', 'Columns', 'cols', 'width']) ?? fallbackDimensions.columns ?? fallbackDimensions.width);
  const rowCosines = normalizeVector3(imageOrientationPatient?.slice(0, 3));
  const columnCosines = normalizeVector3(imageOrientationPatient?.slice(3, 6));
  if (!imagePositionPatient || !rowCosines || !columnCosines || !pixelSpacing || !rows || !columns) return null;
  const normal = normalizeVector3(vectorCross(rowCosines, columnCosines));
  if (!normal || pixelSpacing.some(value => value <= 0)) return null;
  return {
    imagePositionPatient,
    imageOrientationPatient: [...rowCosines, ...columnCosines],
    frameOfReferenceUID,
    pixelSpacing,
    rows,
    columns,
  };
};

export const getTriangulationGeometryReadiness = (source = {}, target = {}) => {
  const missing = [];
  const sourceIpp = parseNumberList(readFirst(source, ['imagePositionPatient', 'ImagePositionPatient', 'imagePosition', 'ipp']), 3);
  const sourceIop = parseNumberList(readFirst(source, ['imageOrientationPatient', 'ImageOrientationPatient', 'imageOrientation', 'iop']), 6);
  const sourcePs = parseNumberList(readFirst(source, ['pixelSpacing', 'PixelSpacing', 'pixel_spacing', 'ImagerPixelSpacing', 'imagerPixelSpacing']), 2);
  const sourceFor = readString(source, ['frameOfReferenceUID', 'FrameOfReferenceUID', 'frameOfReferenceUid', 'forUID', 'forUid']);
  const targetIpp = parseNumberList(readFirst(target, ['imagePositionPatient', 'ImagePositionPatient', 'imagePosition', 'ipp']), 3);
  const targetIop = parseNumberList(readFirst(target, ['imageOrientationPatient', 'ImageOrientationPatient', 'imageOrientation', 'iop']), 6);
  const targetPs = parseNumberList(readFirst(target, ['pixelSpacing', 'PixelSpacing', 'pixel_spacing', 'ImagerPixelSpacing', 'imagerPixelSpacing']), 2);
  const targetFor = readString(target, ['frameOfReferenceUID', 'FrameOfReferenceUID', 'frameOfReferenceUid', 'forUID', 'forUid']);
  if (!sourceIpp) missing.push('source ImagePositionPatient');
  if (!sourceIop) missing.push('source ImageOrientationPatient');
  if (!sourcePs) missing.push('source PixelSpacing');
  if (!sourceFor) missing.push('source FrameOfReferenceUID');
  if (!targetIpp) missing.push('target ImagePositionPatient');
  if (!targetIop) missing.push('target ImageOrientationPatient');
  if (!targetPs) missing.push('target PixelSpacing');
  if (!targetFor) missing.push('target FrameOfReferenceUID');
  if (sourceFor && targetFor && sourceFor !== targetFor) missing.push('matching FrameOfReferenceUID');
  return {
    ready: missing.length === 0,
    missing,
    source: { imagePositionPatient: sourceIpp, imageOrientationPatient: sourceIop, pixelSpacing: sourcePs, frameOfReferenceUID: sourceFor },
    target: { imagePositionPatient: targetIpp, imageOrientationPatient: targetIop, pixelSpacing: targetPs, frameOfReferenceUID: targetFor },
  };
};

export const shouldHandleTriangulationMiddleClick = ({ button, viewportFrozen = false, point, series } = {}) => {
  if (viewportFrozen || Number(button) !== 1) return false;
  if (!series?.studyInstanceUID || !series?.seriesInstanceUID) return false;
  return Boolean(normalizeTriangulationPoint(point, { clamp: false }));
};

export const createTriangulationPoint = ({ sourceViewportIndex, point, slice, total, series, sourceGeometry = null, sourceDimensions = null, timestamp = Date.now() }) => {
  const normalized = normalizeTriangulationPoint(point, { clamp: false });
  if (!normalized || !series?.studyInstanceUID || !series?.seriesInstanceUID) return null;
  const source = Number(sourceViewportIndex);
  const safeSource = Number.isFinite(source) ? source : 0;
  const safeSlice = Number.isFinite(Number(slice)) ? Number(slice) : 0;
  const safeTotal = Number.isFinite(Number(total)) && Number(total) > 0 ? Number(total) : 1;
  const normalizedSourceGeometry = normalizeTriangulationGeometry(sourceGeometry, sourceDimensions || {});
  return {
    sourceViewportIndex: safeSource,
    point: normalized,
    slice: Math.max(0, safeSlice),
    total: safeTotal,
    fraction: safeTotal > 1 ? Math.max(0, Math.min(1, safeSlice / (safeTotal - 1))) : 0,
    studyInstanceUID: series.studyInstanceUID,
    seriesInstanceUID: series.seriesInstanceUID,
    seriesDescription: series.seriesDescription || '',
    sourceGeometry: normalizedSourceGeometry,
    geometryMode: normalizedSourceGeometry ? 'dicom' : 'normalized-fallback',
    timestamp,
  };
};

export const shouldShowTriangulationPoint = (payload, viewportIndex, series) => {
  if (!payload || !series) return false;
  if (payload.sourceViewportIndex === viewportIndex) return false;
  if (payload.studyInstanceUID && series.studyInstanceUID && payload.studyInstanceUID !== series.studyInstanceUID) return false;
  return Boolean(normalizeTriangulationPoint(payload.point));
};

export const shouldShowTriangulationSourceMarker = (payload, viewportIndex, series) => {
  if (!payload || !series) return false;
  if (payload.sourceViewportIndex !== viewportIndex) return false;
  if (payload.studyInstanceUID && series.studyInstanceUID && payload.studyInstanceUID !== series.studyInstanceUID) return false;
  if (payload.seriesInstanceUID && series.seriesInstanceUID && payload.seriesInstanceUID !== series.seriesInstanceUID) return false;
  return Boolean(normalizeTriangulationPoint(payload.point));
};

export const shouldClearTriangulationOnMiddleClick = (payload, viewportIndex, series) => {
  return shouldShowTriangulationSourceMarker(payload, viewportIndex, series);
};

export const createGeometrySnapshot = ({ viewportIndex = 0, slice = 0, total = 1, series = null, geometry = null, seriesGeometry = null, dimensions = null } = {}) => {
  if (!series?.studyInstanceUID || !series?.seriesInstanceUID) return null;
  const normalizedGeometry = normalizeTriangulationGeometry(geometry, dimensions || {});
  if (!normalizedGeometry?.frameOfReferenceUID) return null;
  const safeSlice = Number.isFinite(Number(slice)) ? Number(slice) : 0;
  const safeTotal = Number.isFinite(Number(total)) && Number(total) > 0 ? Number(total) : 1;
  return {
    viewportIndex: Number.isFinite(Number(viewportIndex)) ? Number(viewportIndex) : 0,
    slice: Math.max(0, safeSlice),
    total: safeTotal,
    studyInstanceUID: series.studyInstanceUID,
    seriesInstanceUID: series.seriesInstanceUID,
    seriesDescription: series.seriesDescription || '',
    geometry: normalizedGeometry,
    seriesGeometry: Array.isArray(seriesGeometry) ? seriesGeometry : [],
    dimensions: dimensions || null,
  };
};


export const imagePointToPatientPoint = (sourceGeometry, point, sourceDimensions = {}) => {
  const normalized = normalizeTriangulationPoint(point, { clamp: false });
  const geometry = normalizeTriangulationGeometry(sourceGeometry, sourceDimensions || {});
  if (!normalized || !geometry) return null;
  const rowCosines = geometry.imageOrientationPatient.slice(0, 3);
  const columnCosines = geometry.imageOrientationPatient.slice(3, 6);
  const col = normalized.x * geometry.columns;
  const row = normalized.y * geometry.rows;
  return vectorAdd(
    geometry.imagePositionPatient,
    vectorAdd(
      vectorScale(rowCosines, col * geometry.pixelSpacing[1]),
      vectorScale(columnCosines, row * geometry.pixelSpacing[0]),
    ),
  );
};

const planeNormalForGeometry = (geometry) => {
  const normalized = normalizeTriangulationGeometry(geometry);
  if (!normalized) return null;
  const rowCosines = normalized.imageOrientationPatient.slice(0, 3);
  const columnCosines = normalized.imageOrientationPatient.slice(3, 6);
  return normalizeVector3(vectorCross(rowCosines, columnCosines));
};

const arePlaneNormalsParallel = (sourceGeometry, targetGeometry, threshold = 0.985) => {
  const sourceNormal = planeNormalForGeometry(sourceGeometry);
  const targetNormal = planeNormalForGeometry(targetGeometry);
  if (!sourceNormal || !targetNormal) return false;
  return Math.abs(vectorDot(sourceNormal, targetNormal)) >= threshold;
};

const targetStackIsParallelToSource = (sourceGeometry, targetSeriesGeometry = []) => {
  if (!sourceGeometry || !Array.isArray(targetSeriesGeometry) || !targetSeriesGeometry.length) return false;
  const source = normalizeTriangulationGeometry(sourceGeometry);
  if (!source?.frameOfReferenceUID) return false;
  const targetGeometry = targetSeriesGeometry.find(frame => {
    const normalized = normalizeTriangulationGeometry(frame);
    return normalized?.frameOfReferenceUID === source.frameOfReferenceUID;
  });
  if (!targetGeometry) return false;
  return arePlaneNormalsParallel(source, targetGeometry);
};

export const projectPatientPointToSlice = (targetGeometry, patientPoint, targetDimensions = {}) => {
  const geometry = normalizeTriangulationGeometry(targetGeometry, targetDimensions || {});
  if (!geometry || !Array.isArray(patientPoint) || patientPoint.length !== 3 || patientPoint.some(value => !Number.isFinite(Number(value)))) return null;
  const targetPoint = patientPoint.map(Number);
  const rowCosines = geometry.imageOrientationPatient.slice(0, 3);
  const columnCosines = geometry.imageOrientationPatient.slice(3, 6);
  const normal = normalizeVector3(vectorCross(rowCosines, columnCosines));
  if (!normal) return null;
  const local = vectorSub(targetPoint, geometry.imagePositionPatient);
  const col = vectorDot(local, rowCosines) / geometry.pixelSpacing[1];
  const row = vectorDot(local, columnCosines) / geometry.pixelSpacing[0];
  const distanceMm = vectorDot(local, normal);
  if (![col, row, distanceMm].every(Number.isFinite)) return null;
  const point = { x: col / geometry.columns, y: row / geometry.rows };
  return {
    mode: 'dicom-geometry',
    point,
    inBounds: point.x >= 0 && point.x <= 1 && point.y >= 0 && point.y <= 1,
    distanceMm,
    absDistanceMm: Math.abs(distanceMm),
    frameOfReferenceUID: geometry.frameOfReferenceUID,
    geometry,
  };
};

export const findNearestGeometrySliceForPatientPoint = (seriesGeometry = [], patientPoint, { sourceFrameOfReferenceUID = '', maxDistanceMm = null } = {}) => {
  if (!Array.isArray(seriesGeometry) || !seriesGeometry.length) return { mode: 'missing-geometry', slice: null, reason: 'target series geometry unavailable' };
  let best = null;
  let sawSameFrame = !sourceFrameOfReferenceUID;
  seriesGeometry.forEach((geometry, slice) => {
    const normalized = normalizeTriangulationGeometry(geometry);
    if (!normalized) return;
    if (sourceFrameOfReferenceUID && normalized.frameOfReferenceUID && normalized.frameOfReferenceUID !== sourceFrameOfReferenceUID) return;
    if (sourceFrameOfReferenceUID && !normalized.frameOfReferenceUID) return;
    sawSameFrame = true;
    const projection = projectPatientPointToSlice(normalized, patientPoint);
    if (!projection) return;
    const candidate = { ...projection, slice, targetGeometry: normalized };
    if (!best || candidate.absDistanceMm < best.absDistanceMm) best = candidate;
  });
  if (!sawSameFrame) return { mode: 'frame-mismatch', slice: null, reason: 'FrameOfReferenceUID mismatch' };
  if (!best) return { mode: 'missing-geometry', slice: null, reason: 'no projectable target slice geometry' };
  if (maxDistanceMm != null && Number.isFinite(Number(maxDistanceMm)) && best.absDistanceMm > Number(maxDistanceMm)) {
    return { ...best, mode: 'outside-slice-tolerance', slice: null, reason: 'nearest target slice exceeds tolerance' };
  }
  return best;
};

export const createTriangulationSyncCommands = (payload, viewportGeometries = {}) => {
  const patientPoint = Array.isArray(payload?.patientPoint)
    ? payload.patientPoint.map(Number)
    : imagePointToPatientPoint(payload?.sourceGeometry, payload?.point);
  const sourceFrameOfReferenceUID = payload?.sourceGeometry?.frameOfReferenceUID || payload?.sourceGeometry?.FrameOfReferenceUID || '';
  if (!payload || !patientPoint || patientPoint.some(value => !Number.isFinite(value))) return {};
  return Object.values(viewportGeometries || {}).reduce((commands, target) => {
    if (!target || target.viewportIndex == null) return commands;
    if (target.viewportIndex === payload.sourceViewportIndex) return commands;
    if (payload.studyInstanceUID && target.studyInstanceUID && payload.studyInstanceUID !== target.studyInstanceUID) return commands;
    if (targetStackIsParallelToSource(payload.sourceGeometry, target.seriesGeometry || [])) return commands;
    const nearest = findNearestGeometrySliceForPatientPoint(target.seriesGeometry || [], patientPoint, { sourceFrameOfReferenceUID });
    if (nearest?.mode !== 'dicom-geometry' || nearest.slice == null || !nearest.inBounds) return commands;
    commands[target.viewportIndex] = {
      type: 'triangulation_sync',
      source: 'triangulation',
      sourceViewportIndex: payload.sourceViewportIndex,
      studyInstanceUID: payload.studyInstanceUID,
      sourceSeriesInstanceUID: payload.seriesInstanceUID,
      targetSeriesInstanceUID: target.seriesInstanceUID,
      slice: nearest.slice,
      point: nearest.point,
      distanceMm: nearest.distanceMm,
      mode: nearest.mode,
      nonce: payload.nonce || payload.timestamp || Date.now(),
    };
    return commands;
  }, {});
};


const patientPointFromSource = (payload) => {
  return imagePointToPatientPoint(payload?.sourceGeometry, payload?.point);
};

const projectPatientPointToTarget = (patientPoint, sourceGeometry, targetGeometry, targetDimensions = {}) => {
  const source = normalizeTriangulationGeometry(sourceGeometry);
  const geometry = normalizeTriangulationGeometry(targetGeometry, targetDimensions || {});
  if (!patientPoint || !source || !geometry) return null;
  if (!source.frameOfReferenceUID || !geometry.frameOfReferenceUID || source.frameOfReferenceUID !== geometry.frameOfReferenceUID) return null;
  const rowCosines = geometry.imageOrientationPatient.slice(0, 3);
  const columnCosines = geometry.imageOrientationPatient.slice(3, 6);
  const local = vectorSub(patientPoint, geometry.imagePositionPatient);
  const col = vectorDot(local, rowCosines) / geometry.pixelSpacing[1];
  const row = vectorDot(local, columnCosines) / geometry.pixelSpacing[0];
  if (!Number.isFinite(col) || !Number.isFinite(row)) return null;
  return { x: col / geometry.columns, y: row / geometry.rows };
};

const normalizedProjectionToScreen = (point, overlayImgRect, mode, reason = '') => {
  const normalized = normalizeTriangulationPoint(point);
  if (!normalized || !overlayImgRect || overlayImgRect.width <= 0 || overlayImgRect.height <= 0) return null;
  return {
    x: overlayImgRect.left + normalized.x * overlayImgRect.width,
    y: overlayImgRect.top + normalized.y * overlayImgRect.height,
    mode,
    reason,
    normalized,
  };
};

export const triangulationOverlayProjection = (payload, overlayImgRect, { targetGeometry = null, targetDimensions = null } = {}) => {
  const fallback = () => normalizedProjectionToScreen(payload?.point, overlayImgRect, 'normalized-fallback', 'DICOM geometry unavailable; using normalized image coordinates');
  if (!overlayImgRect || overlayImgRect.width <= 0 || overlayImgRect.height <= 0) return null;
  const patientPoint = patientPointFromSource(payload);
  if (!patientPoint) return fallback();
  const targetPoint = projectPatientPointToTarget(patientPoint, payload?.sourceGeometry, targetGeometry, targetDimensions || {});
  if (!targetPoint) return fallback();
  if (targetPoint.x < 0 || targetPoint.x > 1 || targetPoint.y < 0 || targetPoint.y > 1) {
    return normalizedProjectionToScreen(payload?.point, overlayImgRect, 'normalized-fallback', 'DICOM projection outside target image; using normalized image coordinates');
  }
  return normalizedProjectionToScreen(targetPoint, overlayImgRect, 'dicom-geometry', 'DICOM geometry projection');
};

const projectPatientPointToGeometry = (patientPoint, geometry) => {
  const target = normalizeTriangulationGeometry(geometry);
  if (!patientPoint || !target) return null;
  const rowCosines = target.imageOrientationPatient.slice(0, 3);
  const columnCosines = target.imageOrientationPatient.slice(3, 6);
  const local = vectorSub(patientPoint, target.imagePositionPatient);
  const col = vectorDot(local, rowCosines) / target.pixelSpacing[1];
  const row = vectorDot(local, columnCosines) / target.pixelSpacing[0];
  if (!Number.isFinite(col) || !Number.isFinite(row)) return null;
  return { x: col / target.columns, y: row / target.rows };
};

const clipLineToUnitSquare = (point, direction) => {
  if (!point || !direction || !Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(direction.x) || !Number.isFinite(direction.y)) return null;
  const candidates = [];
  const push = (t) => {
    if (!Number.isFinite(t)) return;
    const x = point.x + direction.x * t;
    const y = point.y + direction.y * t;
    if (x >= -1e-6 && x <= 1 + 1e-6 && y >= -1e-6 && y <= 1 + 1e-6) candidates.push({ x: clamp01(x), y: clamp01(y), t });
  };
  if (Math.abs(direction.x) > 1e-9) {
    push((0 - point.x) / direction.x);
    push((1 - point.x) / direction.x);
  }
  if (Math.abs(direction.y) > 1e-9) {
    push((0 - point.y) / direction.y);
    push((1 - point.y) / direction.y);
  }
  const unique = [];
  for (const candidate of candidates.sort((a, b) => a.t - b.t)) {
    if (!unique.some(item => Math.abs(item.x - candidate.x) < 1e-5 && Math.abs(item.y - candidate.y) < 1e-5)) unique.push(candidate);
  }
  return unique.length >= 2 ? [unique[0], unique[unique.length - 1]] : null;
};

export const referenceLineOverlayProjection = (sourceGeometry, targetGeometry, overlayImgRect, { sourceDimensions = null, targetDimensions = null, label = '' } = {}) => {
  const source = normalizeTriangulationGeometry(sourceGeometry, sourceDimensions || {});
  const target = normalizeTriangulationGeometry(targetGeometry, targetDimensions || {});
  if (!source || !target || !overlayImgRect || overlayImgRect.width <= 0 || overlayImgRect.height <= 0) return null;
  if (!source.frameOfReferenceUID || !target.frameOfReferenceUID || source.frameOfReferenceUID !== target.frameOfReferenceUID) return null;
  const sourceRow = source.imageOrientationPatient.slice(0, 3);
  const sourceColumn = source.imageOrientationPatient.slice(3, 6);
  const targetRow = target.imageOrientationPatient.slice(0, 3);
  const targetColumn = target.imageOrientationPatient.slice(3, 6);
  const sourceNormal = normalizeVector3(vectorCross(sourceRow, sourceColumn));
  const targetNormal = normalizeVector3(vectorCross(targetRow, targetColumn));
  if (!sourceNormal || !targetNormal) return null;
  const direction3d = vectorCross(sourceNormal, targetNormal);
  const directionLength2 = vectorDot(direction3d, direction3d);
  if (!Number.isFinite(directionLength2) || directionLength2 < 1e-8) return null;
  const sourceD = vectorDot(sourceNormal, source.imagePositionPatient);
  const targetD = vectorDot(targetNormal, target.imagePositionPatient);
  const pointOnLine = vectorScale(vectorCross(vectorSub(vectorScale(targetNormal, sourceD), vectorScale(sourceNormal, targetD)), direction3d), 1 / directionLength2);
  const scale = Math.max(target.rows * target.pixelSpacing[0], target.columns * target.pixelSpacing[1], 1);
  const normalizedPoint = projectPatientPointToGeometry(pointOnLine, target);
  const normalizedPoint2 = projectPatientPointToGeometry(vectorAdd(pointOnLine, vectorScale(direction3d, scale)), target);
  if (!normalizedPoint || !normalizedPoint2) return null;
  const clipped = clipLineToUnitSquare(normalizedPoint, { x: normalizedPoint2.x - normalizedPoint.x, y: normalizedPoint2.y - normalizedPoint.y });
  if (!clipped) return null;
  const ordered = [...clipped].sort((left, right) => (left.x - right.x) || (left.y - right.y));
  const [a, b] = ordered;
  return {
    x1: overlayImgRect.left + a.x * overlayImgRect.width,
    y1: overlayImgRect.top + a.y * overlayImgRect.height,
    x2: overlayImgRect.left + b.x * overlayImgRect.width,
    y2: overlayImgRect.top + b.y * overlayImgRect.height,
    mode: 'dicom-geometry',
    reason: 'DICOM reference line projection',
    label,
    normalized: { a, b },
  };
};

export const triangulationOverlayPoint = (payload, overlayImgRect, options = {}) => {
  const projection = triangulationOverlayProjection(payload, overlayImgRect, options);
  return projection ? { x: projection.x, y: projection.y } : null;
};
